import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

import { env } from '@/env.mjs'

interface TurnstileContextValue {
  token: string | null
  isLoading: boolean
  error: string | null
  startTurnstile: () => void
  stopTurnstile: () => void
  resetToken: () => void
  isTurnstileEnabled: boolean
}

const TurnstileContext = createContext<TurnstileContextValue | undefined>(
  undefined
)

export const TurnstileProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [token, setToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const siteKey = env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || ''
  const isTurnstileEnabled = !!siteKey
  const [isActive, setIsActive] = useState(isTurnstileEnabled)
  const widgetIdRef = useRef<string | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)

  const onSuccess = useCallback((token: string) => {
    setToken(token)
    setIsLoading(false)
    setError(null)
    if (!window.ikea?.recommerce) {
      window.ikea.recommerce = {}
    }
    window.ikea.recommerce.latestTurnstileToken = token
  }, [])

  const onError = useCallback((error: string) => {
    setError(`Turnstile error: ${error}`)
    setIsLoading(false)
    setToken(null)
    if (window.ikea?.recommerce) {
      window.ikea.recommerce.latestTurnstileToken = undefined
    }

    // Auto-retry on error after 5 seconds
    setTimeout(() => {
      if (widgetIdRef.current && window.ikea?.recommerce?.turnstile) {
        setIsLoading(true)
        window.ikea?.recommerce.turnstile.reset(widgetIdRef.current)
      }
    }, 5000)
  }, [])

  const onExpire = useCallback(() => {
    setToken(null)
    setError(null)
    if (window.ikea?.recommerce) {
      window.ikea.recommerce.latestTurnstileToken = undefined
    }

    // Auto-refresh on expiration
    if (widgetIdRef.current && window.ikea?.recommerce?.turnstile) {
      setIsLoading(true)
      window.ikea?.recommerce.turnstile.reset(widgetIdRef.current)
    }
  }, [])

  const renderWidget = useCallback(() => {
    if (
      !window.ikea?.recommerce?.turnstile ||
      !containerRef.current ||
      !siteKey ||
      !isActive
    ) {
      return
    }

    // Clean up existing widget
    if (widgetIdRef.current) {
      try {
        window.ikea?.recommerce.turnstile.remove(widgetIdRef.current)
      } catch (err) {
        console.warn('Error removing existing widget:', err)
      }
      widgetIdRef.current = null
    }

    setIsLoading(true)
    setError(null)

    try {
      widgetIdRef.current = window.ikea?.recommerce.turnstile.render(
        containerRef.current,
        {
          sitekey: siteKey,
          callback: onSuccess,
          'error-callback': onError,
          'expired-callback': onExpire,
          theme: 'light',
          size: 'normal',
          execution: 'render',
          appearance: 'execute',
        }
      )
    } catch (err) {
      setError(`Failed to render Turnstile: ${err}`)
      setIsLoading(false)
    }
  }, [siteKey, onSuccess, onError, onExpire, isActive])

  const startTurnstile = useCallback(() => {
    setIsActive(true)
  }, [])

  const stopTurnstile = useCallback(() => {
    setIsActive(false)
    if (widgetIdRef.current && window.ikea?.recommerce?.turnstile) {
      try {
        window.ikea?.recommerce.turnstile.remove(widgetIdRef.current)
      } catch (err) {
        console.warn('Error during cleanup:', err)
      }
      widgetIdRef.current = null
    }
    setToken(null)
    setError(null)
    setIsLoading(false)
    if (window.ikea?.recommerce) {
      window.ikea.recommerce.latestTurnstileToken = undefined
    }
  }, [])

  const resetToken = useCallback(() => {
    setToken(null)
    if (window.ikea?.recommerce) {
      window.ikea.recommerce.latestTurnstileToken = undefined
    }
    // Always re-render the widget to ensure a fresh token is generated
    // This is more reliable than reset() which can silently fail
    renderWidget()
  }, [renderWidget])

  // Render widget when activated
  useEffect(() => {
    if (!siteKey) {
      console.warn(
        'Turnstile: No site key configured. Turnstile will not be initialized.'
      )
      return
    }

    if (isActive && siteKey) {
      renderWidget()
    }
  }, [isActive, siteKey, renderWidget])

  return (
    <TurnstileContext.Provider
      value={{
        token,
        isLoading,
        error,
        startTurnstile,
        stopTurnstile,
        resetToken,
        isTurnstileEnabled,
      }}
    >
      {/* Hidden container for the invisible widget */}
      {isActive && siteKey && (
        <div
          ref={containerRef}
          style={{
            position: 'fixed',
            left: '-9999px',
            top: '-9999px',
            visibility: 'hidden',
            pointerEvents: 'none',
          }}
        />
      )}
      {children}
    </TurnstileContext.Provider>
  )
}

export const useTurnstileContext = () => {
  const context = useContext(TurnstileContext)
  if (context === undefined) {
    throw new Error('useTurnstileContext must be used within TurnstileProvider')
  }
  return context
}
